"use client";

import React, { useRef, useState } from "react";
import {
  Badge,
  Container,
  Logo,
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from "@repo/ui";
import { ShoppingCartOutlined } from "@spoctech/icons";
import Link from "next/link";
import useRouteChange from "@/hooks/useRouteChange";
import { useAppSelector } from "@/store/hooks";
import { Menu, X } from "lucide-react";
import isEmpty from "lodash/isEmpty";

import { useCountryCode } from "@/hooks/useCountryCode";

const OrderHeader = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const cartItem = useAppSelector((state) => state.cart.cartItem);
  const { country, paramsHasCountry } = useCountryCode();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useRouteChange({
    onChangeComplete: () => {
      setMobileMenuOpen(false);
    },
  });

  return (
    <header className="bg-white shadow-md fixed top-0 left-0 right-0 z-[999] min-h-16 flex items-center">
      <Container className="relative" ref={containerRef}>
        <div className="flex items-center gap-1.5 sm:gap-3 py-3 justify-center">
          {/* Logo */}
          <Link
            href={paramsHasCountry ? `/${country}` : "/"}
            className="mr-auto"
          >
            <Logo
              title="Spoclearn"
              className="md:w-[175px] sm:w-[165px] w-[150px]"
            />
          </Link>
          {/* Right Icons (always visible, but adjust for mobile) */}
          <div className="flex items-center gap-3 md:gap-4 ml-auto">
            {/* Cart Icon */}
            {!isEmpty(cartItem) && (
              <Badge
                badgeContent={1}
                color="primary"
                render={
                  <Link
                    title="order summary"
                    href={`/${country}/order-summary/`}
                    className="hover:text-primary-light"
                  />
                }
              >
                <ShoppingCartOutlined />
              </Badge>
            )}
          </div>
          {/* Hamburger for Mobile */}
          <button
            className="md:hidden ml-2"
            onClick={() => setMobileMenuOpen(true)}
            title="Menu toggle"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
        {/* Mobile Drawer/Sidebar */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[1000] flex md:hidden">
            <div className="bg-white w-4/5 max-w-xs h-full px-6 py-[1.1rem] flex flex-col">
              <div className="flex items-center justify-between mb-6">
                <Logo title="Spoclearn" width={175} />
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  title="Menu toggle"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <NavigationMenu orientation="vertical">
                <NavigationMenuList>
                  <NavigationMenuItem>
                    <NavigationMenuLink
                      className="!justify-start"
                      render={<Link href="/courses/" />}
                    >
                      Explore Courses
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                  <NavigationMenuItem>
                    <NavigationMenuLink
                      className="!justify-start"
                      render={<Link href="/corporate-training" />}
                    >
                      Corporate Training
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                  <NavigationMenuItem>
                    <NavigationMenuLink
                      className="!justify-start"
                      render={<Link href="/one-to-one-training/" />}
                    >
                      1 to 1 Training
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                  <NavigationMenuItem>
                    <NavigationMenuLink
                      className="!justify-start"
                      render={<Link href="/contact-us/" />}
                    >
                      Contact us
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>
            </div>
            {/* Click outside to close */}
            <div className="flex-1" onClick={() => setMobileMenuOpen(false)} />
          </div>
        )}
      </Container>
    </header>
  );
};

export default OrderHeader;
